/**
 * BluetoothPrinterService.ts
 * ─────────────────────────
 * Web Bluetooth API integration for ESC/POS thermal receipt printers.
 * Supports any BT thermal printer (Epson, Rongta, GOOJPRT, generic 58/80mm).
 *
 * Browser requirement: Chrome or Edge (Web Bluetooth not supported in Firefox/Safari).
 */

// ── ESC/POS command constants ─────────────────────────────────────────────────
const ESC = 0x1b;
const GS  = 0x1d;
const LF  = 0x0a;
const CR  = 0x0d;

const CMD = {
  INIT:          [ESC, 0x40],                    // Initialize printer
  ALIGN_LEFT:    [ESC, 0x61, 0x00],
  ALIGN_CENTER:  [ESC, 0x61, 0x01],
  ALIGN_RIGHT:   [ESC, 0x61, 0x02],
  BOLD_ON:       [ESC, 0x45, 0x01],
  BOLD_OFF:      [ESC, 0x45, 0x00],
  DOUBLE_HEIGHT: [ESC, 0x21, 0x10],             // Double height text
  NORMAL_SIZE:   [ESC, 0x21, 0x00],
  FONT_SMALL:    [ESC, 0x4d, 0x01],             // Font B (smaller)
  FONT_NORMAL:   [ESC, 0x4d, 0x00],             // Font A (normal)
  CUT:           [GS, 0x56, 0x41, 0x10],        // Partial cut
  FEED_LINES:    (n: number) => [ESC, 0x64, n], // Feed n lines
  LINE_SPACING:  [ESC, 0x33, 0x18],             // 24-dot line spacing
};

// ── Known GATT Service / Characteristic UUIDs ────────────────────────────────
// Covers ~95% of BT thermal printers sold under generic brands
const PRINTER_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb', // Generic ESC/POS BT serial
  '0000ff00-0000-1000-8000-00805f9b34fb', // Generic BT SPP profile
  '00001101-0000-1000-8000-00805f9b34fb', // BT Serial Port Profile (classic)
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Rongta / EPSON TM-P series
  '000018f0-0000-1000-8000-00805f9b34fb', // RPP02N / POS-5890 / TP-58
];

const PRINTER_CHARACTERISTICS = [
  '00002af1-0000-1000-8000-00805f9b34fb', // Generic write char
  '0000ff02-0000-1000-8000-00805f9b34fb', // Generic write char 2
  '00002a19-0000-1000-8000-00805f9b34fb', // BT battery / write
  'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f', // Nordic UART TX
  '49535343-8841-43f4-a8d4-ecbe34729bb3', // ISSC UART TX
];

// ── Types ─────────────────────────────────────────────────────────────────────
export interface BillData {
  restaurantName: string;
  restaurantAddress?: string;
  restaurantPhone?: string;
  billNumber: string;
  date: string;
  customerName?: string;
  customerPhone?: string;
  tableNumber?: string;
  items: Array<{
    name: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    specialInstructions?: string;
  }>;
  notes?: string;
  subtotal: number;
  tax: number;
  discount: number;
  discountLabel?: string;
  total: number;
  paymentMethod?: string;
  tenderedAmount?: number;
  changeAmount?: number;
  footerNote?: string;
}

export interface PrinterStatus {
  connected: boolean;
  deviceName?: string;
  error?: string;
}

// ── Encoder: text → Uint8Array ────────────────────────────────────────────────
function encode(text: string): Uint8Array {
  // TextEncoder gives us UTF-8; ESC/POS printers generally accept ASCII/Latin-1
  return new TextEncoder().encode(text);
}

function bytes(...args: Array<number | number[]>): Uint8Array {
  const flat: number[] = [];
  for (const a of args) {
    if (Array.isArray(a)) flat.push(...a);
    else flat.push(a);
  }
  return new Uint8Array(flat);
}

// ── Print width helpers (58mm ≈ 32 chars, 80mm ≈ 48 chars) ───────────────────
const WIDTH = 32; // default 58mm thermal printer

function padLeft(str: string, width: number): string {
  return str.slice(0, width).padStart(width);
}

function padRight(str: string, width: number): string {
  return str.slice(0, width).padEnd(width);
}

function separator(char = '-'): string {
  return char.repeat(WIDTH) + '\n';
}

function dualColumn(left: string, right: string, totalWidth = WIDTH): string {
  const rightTrimmed = right.slice(0, 12);
  const leftWidth = totalWidth - rightTrimmed.length;
  return padRight(left.slice(0, leftWidth - 1), leftWidth) + rightTrimmed + '\n';
}

function tripleColumn(name: string, qty: string, amount: string): string {
  // name(18) qty(4) amount(10)
  const n = padRight(name, 18);
  const q = padLeft(qty, 4);
  const a = padLeft(amount, 10);
  return n + q + a + '\n';
}

function centerText(text: string, width = WIDTH): string {
  const trimmed = text.slice(0, width);
  const pad = Math.max(0, Math.floor((width - trimmed.length) / 2));
  return ' '.repeat(pad) + trimmed + '\n';
}

// ── Build the ESC/POS receipt byte stream ─────────────────────────────────────
function buildReceiptBytes(bill: BillData): Uint8Array {
  const chunks: Uint8Array[] = [];

  const push = (...args: Array<Uint8Array | number[]>) => {
    for (const a of args) {
      chunks.push(a instanceof Uint8Array ? a : new Uint8Array(a));
    }
  };

  const text = (s: string) => { push(encode(s)); };
  const nl = () => { push(new Uint8Array([LF])); };

  // ── Initialize
  push(CMD.INIT, CMD.LINE_SPACING);

  // ── Header: Restaurant name
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON, CMD.DOUBLE_HEIGHT);
  text(bill.restaurantName.slice(0, WIDTH) + '\n');
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);

  if (bill.restaurantAddress) {
    text(centerText(bill.restaurantAddress));
  }
  if (bill.restaurantPhone) {
    text(centerText(`Ph: ${bill.restaurantPhone}`));
  }

  push(CMD.ALIGN_LEFT);
  text(separator('='));

  // ── Bill info
  push(CMD.FONT_SMALL);
  text(dualColumn('Bill No:', bill.billNumber));
  text(dualColumn('Date:', bill.date));
  if (bill.tableNumber) {
    text(dualColumn('Table:', `T-${bill.tableNumber}`));
  }
  if (bill.customerName) {
    text(dualColumn('Customer:', bill.customerName));
  }
  if (bill.customerPhone) {
    text(dualColumn('Phone:', bill.customerPhone));
  }
  push(CMD.FONT_NORMAL);

  text(separator('-'));

  // ── Column headers
  push(CMD.BOLD_ON);
  text(tripleColumn('Item', 'Qty', 'Amount'));
  push(CMD.BOLD_OFF);
  text(separator('-'));

  // ── Items
  push(CMD.FONT_SMALL);
  for (const item of bill.items) {
    text(tripleColumn(
      item.name,
      `x${item.quantity}`,
      `Rs${item.totalPrice.toFixed(2)}`,
    ));
    if (item.specialInstructions) {
      text(`  * ${item.specialInstructions.slice(0, 28)}\n`);
    }
  }
  push(CMD.FONT_NORMAL);

  if (bill.notes) {
    text(separator('-'));
    push(CMD.FONT_SMALL);
    text(`Note: ${bill.notes.slice(0, 54)}\n`);
    push(CMD.FONT_NORMAL);
  }

  text(separator('-'));

  // ── Totals
  text(dualColumn('Subtotal:', `Rs${bill.subtotal.toFixed(2)}`));
  text(dualColumn('GST (18%):', `Rs${bill.tax.toFixed(2)}`));

  if (bill.discount > 0) {
    const label = bill.discountLabel ? `Disc(${bill.discountLabel.slice(0, 6)}):` : 'Discount:';
    text(dualColumn(label, `-Rs${bill.discount.toFixed(2)}`));
  }

  text(separator('='));

  // ── Grand total
  push(CMD.BOLD_ON, CMD.DOUBLE_HEIGHT);
  text(dualColumn('TOTAL:', `Rs${bill.total.toFixed(0)}`));
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);

  text(separator('='));

  // ── Payment info
  if (bill.paymentMethod) {
    text(dualColumn('Payment:', bill.paymentMethod.toUpperCase()));
  }
  if (bill.tenderedAmount !== undefined && bill.paymentMethod?.toLowerCase() === 'cash') {
    text(dualColumn('Tendered:', `Rs${bill.tenderedAmount.toFixed(0)}`));
    text(dualColumn('Change:', `Rs${(bill.changeAmount ?? 0).toFixed(0)}`));
  }

  text(separator('-'));

  // ── Footer
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON);
  text(centerText(bill.footerNote ?? 'Thank you! Visit again!'));
  push(CMD.BOLD_OFF);
  text(centerText('Powered by MenuMaster POS'));
  push(CMD.ALIGN_LEFT);

  // ── Feed & cut
  push(CMD.FEED_LINES(4), CMD.CUT);

  // Merge all chunks
  const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

// ── KOT byte stream ───────────────────────────────────────────────────────────
function buildKOTBytes(
  items: Array<{ name: string; quantity: number; specialInstructions?: string }>,
  tableNumber?: string,
  billName?: string,
  orderNotes?: string,
): Uint8Array {
  const chunks: Uint8Array[] = [];
  const push = (...args: Array<Uint8Array | number[]>) => {
    for (const a of args) {
      chunks.push(a instanceof Uint8Array ? a : new Uint8Array(a));
    }
  };
  const text = (s: string) => { push(encode(s)); };

  push(CMD.INIT, CMD.ALIGN_CENTER, CMD.BOLD_ON, CMD.DOUBLE_HEIGHT);
  text('** KITCHEN ORDER **\n');
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF, CMD.ALIGN_LEFT);
  text(separator('='));

  const now = new Date();
  text(dualColumn('Time:', now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })));
  if (tableNumber) text(dualColumn('Table:', `T-${tableNumber}`));
  if (billName) text(dualColumn('Bill:', billName));

  text(separator('-'));

  push(CMD.BOLD_ON, CMD.DOUBLE_HEIGHT);
  for (const item of items) {
    text(`  x${item.quantity}  ${item.name.slice(0, 22)}\n`);
    if (item.specialInstructions) {
      push(CMD.NORMAL_SIZE);
      text(`     -> ${item.specialInstructions.slice(0, 24)}\n`);
      push(CMD.DOUBLE_HEIGHT);
    }
  }
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);

  if (orderNotes) {
    text(separator('-'));
    push(CMD.FONT_SMALL);
    text(`Note: ${orderNotes.slice(0, 54)}\n`);
    push(CMD.FONT_NORMAL);
  }

  text(separator('='));
  push(CMD.FEED_LINES(4), CMD.CUT);

  const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

function buildTestReceiptBytes(printerName = 'Bluetooth Printer'): Uint8Array {
  const now = new Date().toLocaleString('en-IN');
  const content = [
    '\x1b@',
    '\x1ba\x01',
    '\x1bE\x01',
    'MENUMASTER POS\n',
    '\x1bE\x00',
    'PRINTER TEST RECEIPT\n',
    '--------------------------------\n',
    `Printer: ${printerName.slice(0, 24)}\n`,
    `Time: ${now}\n`,
    'Status: Connection OK\n',
    '--------------------------------\n',
    'If you can read this, printing works.\n',
    '\n\n\n',
  ].join('');
  return encode(content);
}

// ── Main Service Class ────────────────────────────────────────────────────────
class BluetoothPrinterService {
  private device: BluetoothDevice | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private onStatusChange: ((status: PrinterStatus) => void) | null = null;

  /** Register a callback to receive connection status updates */
  setStatusCallback(cb: (status: PrinterStatus) => void) {
    this.onStatusChange = cb;
  }

  private notify(status: PrinterStatus) {
    this.onStatusChange?.(status);
  }

  get isConnected(): boolean {
    return !!(this.device?.gatt?.connected && this.characteristic);
  }

  get deviceName(): string | undefined {
    return this.device?.name ?? undefined;
  }

  /** Returns true if the current browser supports Web Bluetooth */
  static isSupported(): boolean {
    return 'bluetooth' in navigator;
  }

  /** Open the browser Bluetooth picker and connect to a printer */
  async connect(): Promise<PrinterStatus> {
    if (!BluetoothPrinterService.isSupported()) {
      const err = 'Web Bluetooth is not supported in this browser. Please use Chrome or Edge.';
      this.notify({ connected: false, error: err });
      return { connected: false, error: err };
    }

    try {
      // Request any BT device — we'll discover services after connecting
      this.device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: PRINTER_SERVICES,
      });

      this.device!.addEventListener('gattserverdisconnected', () => {
        this.characteristic = null;
        this.notify({ connected: false, deviceName: this.device?.name });
      });

      const server = await this.device!.gatt!.connect();
      this.characteristic = await this.discoverWriteCharacteristic(server);

      if (!this.characteristic) {
        throw new Error('No writable characteristic found. Is this a thermal printer?');
      }

      const status: PrinterStatus = { connected: true, deviceName: this.device!.name ?? 'BT Printer' };
      this.notify(status);
      return status;
    } catch (err: any) {
      const msg = err?.message ?? String(err);
      if (msg.includes('User cancelled')) {
        const cancelled = { connected: false, error: 'Bluetooth picker cancelled' };
        this.notify(cancelled);
        return cancelled;
      }
      const status: PrinterStatus = { connected: false, error: msg };
      this.notify(status);
      return status;
    }
  }

  /** Reconnect to the previously paired device (no picker shown) */
  async reconnect(): Promise<boolean> {
    if (!this.device) return false;
    try {
      const server = await this.device.gatt!.connect();
      this.characteristic = await this.discoverWriteCharacteristic(server);
      if (this.characteristic) {
        this.notify({ connected: true, deviceName: this.device.name ?? 'BT Printer' });
        return true;
      }
    } catch {
      // silent fail — user can manually reconnect
    }
    return false;
  }

  /** Disconnect from printer */
  disconnect() {
    this.device?.gatt?.disconnect();
    this.characteristic = null;
    this.device = null;
    this.notify({ connected: false });
  }

  /** Try to find a writable GATT characteristic on the connected server */
  private async discoverWriteCharacteristic(
    server: BluetoothRemoteGATTServer,
  ): Promise<BluetoothRemoteGATTCharacteristic | null> {
    const candidates: BluetoothRemoteGATTCharacteristic[] = [];
    const seen = new Set<string>();
    const addCandidates = (chars: BluetoothRemoteGATTCharacteristic[]) => {
      chars.forEach((char) => {
        const uuid = String((char as BluetoothRemoteGATTCharacteristic & { uuid?: string }).uuid ?? '').toLowerCase();
        // Never select the standard battery characteristic as the print pipe.
        if (uuid === '00002a19-0000-1000-8000-00805f9b34fb') return;
        if ((char.properties.write || char.properties.writeWithoutResponse) && !seen.has(uuid)) {
          seen.add(uuid);
          candidates.push(char);
        }
      });
    };

    // Try each known service UUID
    for (const serviceUUID of PRINTER_SERVICES) {
      try {
        const service = await server.getPrimaryService(serviceUUID);
        addCandidates(await service.getCharacteristics());
      } catch {
        // Service not present on this device — try next
      }
    }

    // Fallback: enumerate all primary services
    try {
      const services = await server.getPrimaryServices();
      for (const service of services) {
        try {
          addCandidates(await service.getCharacteristics());
        } catch {
          // skip
        }
      }
    } catch {
      // skip
    }

    candidates.sort((a, b) => {
      const score = (char: BluetoothRemoteGATTCharacteristic) => {
        const uuid = String((char as BluetoothRemoteGATTCharacteristic & { uuid?: string }).uuid ?? '').toLowerCase();
        const known = PRINTER_CHARACTERISTICS.some((item) => item.toLowerCase() === uuid);
        return (known ? 100 : 0) + (char.properties.write ? 20 : 0) + (char.properties.writeWithoutResponse ? 10 : 0);
      };
      return score(b) - score(a);
    });

    return candidates[0] ?? null;
  }

  /** Write using conservative BLE packets; generic printers often only accept 20 bytes. */
  private async writeBytes(data: Uint8Array): Promise<void> {
    if (!this.characteristic) throw new Error('Printer not connected');

    // Some printers advertise a larger MTU but fail on large writes with the
    // opaque Chrome error: "GATT operation failed for unknown reason".
    const CHUNK = 20;
    for (let offset = 0; offset < data.length; offset += CHUNK) {
      const chunk = data.slice(offset, offset + CHUNK);
      let lastError: unknown;

      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          if (!this.characteristic || !this.device?.gatt?.connected) {
            const reconnected = await this.reconnect();
            if (!reconnected) throw new Error('Printer disconnected during printing');
          }
          // Prefer acknowledged writes when the printer supports them. They
          // are slower but much more reliable on Windows and Android BLE.
          if (this.characteristic!.properties.write) {
            await this.characteristic!.writeValue(chunk);
          } else {
            await this.characteristic!.writeValueWithoutResponse(chunk);
          }
          lastError = undefined;
          break;
        } catch (error) {
          lastError = error;
          await new Promise((r) => setTimeout(r, attempt * 120));
        }
      }

      if (lastError) {
        const message = lastError instanceof Error ? lastError.message : String(lastError);
        throw new Error(`Printer write failed. Keep the printer awake and reconnect it. ${message}`);
      }
      // Give small thermal BLE printers time to drain their receive buffer.
      await new Promise((r) => setTimeout(r, 35));
    }
  }

  /** Print a full bill receipt */
  async printBill(bill: BillData): Promise<void> {
    if (!this.isConnected) {
      // Try to reconnect silently
      const ok = await this.reconnect();
      if (!ok) throw new Error('Printer not connected. Please connect first.');
    }
    const receiptBytes = buildReceiptBytes(bill);
    await this.writeBytes(receiptBytes);
  }

  /** Print a Kitchen Order Ticket */
  async printKOT(
    items: Array<{ name: string; quantity: number; specialInstructions?: string }>,
    tableNumber?: string,
    billName?: string,
    orderNotes?: string,
  ): Promise<void> {
    if (!this.isConnected) {
      const ok = await this.reconnect();
      if (!ok) throw new Error('Printer not connected. Please connect first.');
    }
    const kotBytes = buildKOTBytes(items, tableNumber, billName, orderNotes);
    await this.writeBytes(kotBytes);
  }

  /** Print a small diagnostic receipt without requiring an order or bill. */
  async printTestReceipt(): Promise<void> {
    if (!this.isConnected) {
      const ok = await this.reconnect();
      if (!ok) throw new Error('Printer not connected. Please connect first.');
    }
    await this.writeBytes(buildTestReceiptBytes(this.deviceName ?? 'Bluetooth Printer'));
  }
}

// ── Singleton export ──────────────────────────────────────────────────────────
const bluetoothPrinter = new BluetoothPrinterService();
export default bluetoothPrinter;
export { BluetoothPrinterService, buildReceiptBytes, buildKOTBytes, buildTestReceiptBytes };
